import { Point, Wall, WindowElement, DoorElement, FloorPlanElement } from '../types';

// Convert degrees to cardinal/intercardinal directions
export const degreesToDirection = (degrees: number): string => {
  const normalized = ((degrees % 360) + 360) % 360;
  
  if (normalized >= 337.5 || normalized < 22.5) return 'N';
  if (normalized >= 22.5 && normalized < 67.5) return 'NE';
  if (normalized >= 67.5 && normalized < 112.5) return 'E';
  if (normalized >= 112.5 && normalized < 157.5) return 'SE';
  if (normalized >= 157.5 && normalized < 202.5) return 'S';
  if (normalized >= 202.5 && normalized < 247.5) return 'SW';
  if (normalized >= 247.5 && normalized < 292.5) return 'W';
  if (normalized >= 292.5 && normalized < 337.5) return 'NW';
  return 'N';
};

// Calculate wall angle in degrees
export const calculateWallAngle = (wall: Wall): number => {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  
  // Calculate angle in radians and convert to degrees
  let angle = Math.atan2(dy, dx) * (180 / Math.PI);
  
  // Normalize to 0-360 range
  angle = ((angle % 360) + 360) % 360;
  
  return angle;
};

// Determine which side of a wall faces the exterior (outward direction)
// This uses building perimeter analysis to determine the "outside" direction
export const calculateWallOutwardDirection = (wall: Wall, allWalls?: Wall[]): number => {
  const wallAngle = calculateWallAngle(wall);
  
  // Calculate both perpendicular directions (90 degrees from wall direction)
  const perp1 = (wallAngle + 90) % 360;
  const perp2 = (wallAngle - 90 + 360) % 360;
  
  // If we have access to all walls, use building perimeter analysis
  if (allWalls && allWalls.length > 1) {
    const wallCenter = {
      x: (wall.start.x + wall.end.x) / 2,
      y: (wall.start.y + wall.end.y) / 2
    };
    
    // Get building bounds to determine which direction is more likely to be outside
    const allPoints = allWalls.flatMap(w => [w.start, w.end]);
    const minX = Math.min(...allPoints.map(p => p.x));
    const maxX = Math.max(...allPoints.map(p => p.x));
    const minY = Math.min(...allPoints.map(p => p.y));
    const maxY = Math.max(...allPoints.map(p => p.y));
    
    const buildingCenter = {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2
    };
    
    // Test both perpendicular directions 
    const testDistance = 100; // pixels
    
    const testPoint1 = {
      x: wallCenter.x + Math.cos(perp1 * Math.PI / 180) * testDistance,
      y: wallCenter.y + Math.sin(perp1 * Math.PI / 180) * testDistance
    };
    
    const testPoint2 = {
      x: wallCenter.x + Math.cos(perp2 * Math.PI / 180) * testDistance,
      y: wallCenter.y + Math.sin(perp2 * Math.PI / 180) * testDistance
    };
    
    // Calculate distance from building center for each test point
    const distToCenter1 = Math.hypot(testPoint1.x - buildingCenter.x, testPoint1.y - buildingCenter.y);
    const distToCenter2 = Math.hypot(testPoint2.x - buildingCenter.x, testPoint2.y - buildingCenter.y);
    
    // Also count wall intersections as a secondary criterion
    let intersections1 = 0;
    let intersections2 = 0;
    
    for (const otherWall of allWalls) {
      if (otherWall === wall) continue;
      
      if (lineIntersectsWall(wallCenter, testPoint1, otherWall)) {
        intersections1++;
      }
      if (lineIntersectsWall(wallCenter, testPoint2, otherWall)) {
        intersections2++;
      }
    }
    
    // Primary criterion: fewer intersections (more likely to be outside)
    // Secondary criterion: farther from building center
    let outwardAngle: number;
    
    if (intersections1 !== intersections2) {
      // Clear winner based on intersections
      outwardAngle = intersections1 < intersections2 ? perp1 : perp2;
    } else {
      // Tie-breaker: choose direction that goes farther from building center
      outwardAngle = distToCenter1 > distToCenter2 ? perp1 : perp2;
    }
    
    console.log(`🧭 Wall outward calculation: Wall angle=${wallAngle.toFixed(1)}°, perp1=${perp1.toFixed(1)}° (${intersections1} intersections, ${distToCenter1.toFixed(1)}px from center), perp2=${perp2.toFixed(1)}° (${intersections2} intersections, ${distToCenter2.toFixed(1)}px from center), chosen=${outwardAngle.toFixed(1)}°`);
    
    return outwardAngle;
  }
  
  // Fallback: assume the wall faces "right" from its direction vector
  return (wallAngle + 90) % 360;
};

// Helper function to check if a line intersects with a wall
const lineIntersectsWall = (lineStart: Point, lineEnd: Point, wall: Wall): boolean => {
  // Simple line intersection test
  const x1 = lineStart.x, y1 = lineStart.y;
  const x2 = lineEnd.x, y2 = lineEnd.y;
  const x3 = wall.start.x, y3 = wall.start.y;
  const x4 = wall.end.x, y4 = wall.end.y;
  
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return false; // Lines are parallel
  
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
};

// Calculate window orientation based ONLY on compass reading and front door wall relationship
export const calculateWindowOrientation = (
  window: WindowElement, 
  wall: Wall, 
  propertyOrientation: number,
  allWalls?: Wall[],
  frontDoorWall?: Wall
): string => {
  // PURE COMPASS + FRONT DOOR APPROACH:
  // 1. Find the front door wall (the reference wall)
  // 2. Calculate this window's wall direction relative to the front door wall
  // 3. Apply the compass reading to get absolute direction
  
  if (!allWalls) {
    console.warn('🧭 No walls provided for orientation calculation');
    return 'Unknown';
  }
  
  // 🔧 ENHANCED EXTRUSION FIX: Detect if window is on an extrusion wall and find the original wall
  let orientationWall = wall;
  const isExtrusionWall = (wall as any).isMainExtrusionSegment || (wall as any).isExtrusionConnector;
  
  if (isExtrusionWall) {
    console.log('🧭 EXTRUSION DETECTION: Window is on extrusion wall, finding original wall for orientation', {
      windowId: window.id,
      extrusionWallType: (wall as any).isMainExtrusionSegment ? 'main segment' : 'connector',
      extrusionWallAngle: calculateWallAngle(wall).toFixed(1) + '°',
      parentWallId: (wall as any).parentWallId,
      parentRoomIds: (wall as any).parentRoomIds
    });
    
    // Strategy 1: Use parentWallId to find the original wall
    const parentWallId = (wall as any).parentWallId;
    if (parentWallId && allWalls) {
      // Look for walls that could be the original wall based on parentWallId
      const originalWallCandidates = allWalls.filter(w => {
        // Check if this wall has the same parentWallId (split sibling)
        const wallParentId = (w as any).parentWallId;
        if (wallParentId === parentWallId) {
          // Prefer walls that are not extrusion parts
          return !(w as any).isMainExtrusionSegment && !(w as any).isExtrusionConnector;
        }
        return false;
      });
      
      if (originalWallCandidates.length > 0) {
        orientationWall = originalWallCandidates[0];
        console.log('🧭 EXTRUSION FIX: Found original wall using parentWallId', {
          originalWallAngle: calculateWallAngle(orientationWall).toFixed(1) + '°',
          parentWallId: parentWallId
        });
      }
    }
    
    // Strategy 2: If no parentWallId match, look for connected non-extrusion walls
    if (orientationWall === wall) {
      const connectedWalls = allWalls.filter(w => {
        // Skip extrusion walls
        if ((w as any).isMainExtrusionSegment || (w as any).isExtrusionConnector) return false;
        
        // Find walls connected to the extrusion wall
        const tolerance = 1.0;
        return (
          Math.hypot(w.start.x - wall.start.x, w.start.y - wall.start.y) < tolerance ||
          Math.hypot(w.start.x - wall.end.x, w.start.y - wall.end.y) < tolerance ||
          Math.hypot(w.end.x - wall.start.x, w.end.y - wall.start.y) < tolerance ||
          Math.hypot(w.end.x - wall.end.x, w.end.y - wall.end.y) < tolerance
        );
      });
      
      if (connectedWalls.length > 0) {
        // Use the first connected non-extrusion wall
        orientationWall = connectedWalls[0];
        console.log('🧭 EXTRUSION FIX: Found original wall using geometric connection', {
          originalWallAngle: calculateWallAngle(orientationWall).toFixed(1) + '°',
          connectedWallsFound: connectedWalls.length
        });
      }
    }
    
    // Strategy 3: If still no match, look for walls with similar direction but longer length
    if (orientationWall === wall) {
      const extrusionAngle = calculateWallAngle(wall);
      const similarDirectionWalls = allWalls.filter(w => {
        // Skip extrusion walls
        if ((w as any).isMainExtrusionSegment || (w as any).isExtrusionConnector) return false;
        
        // Find walls with similar direction (within 15 degrees)
        const wallAngle = calculateWallAngle(w);
        const angleDiff = Math.abs(extrusionAngle - wallAngle);
        const normalizedAngleDiff = Math.min(angleDiff, 360 - angleDiff);
        
        // Check if wall is longer than the extrusion (likely to be the original)
        const wallLength = Math.hypot(w.end.x - w.start.x, w.end.y - w.start.y);
        const extrusionLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
        
        return normalizedAngleDiff < 15 && wallLength > extrusionLength;
      });
      
      if (similarDirectionWalls.length > 0) {
        // Use the longest wall with similar direction
        orientationWall = similarDirectionWalls.reduce((longest, current) => {
          const currentLength = Math.hypot(current.end.x - current.start.x, current.end.y - current.start.y);
          const longestLength = Math.hypot(longest.end.x - longest.start.x, longest.end.y - longest.start.y);
          return currentLength > longestLength ? current : longest;
        });
        
        console.log('🧭 EXTRUSION FIX: Found original wall using similar direction + length heuristic', {
          originalWallAngle: calculateWallAngle(orientationWall).toFixed(1) + '°',
          similarDirectionWallsFound: similarDirectionWalls.length
        });
      }
    }
    
    // Final fallback: warn if we couldn't find a better wall
    if (orientationWall === wall) {
      console.warn('🧭 EXTRUSION WARNING: Could not find original wall for extrusion, using extrusion wall angle - orientation may be incorrect', {
        windowId: window.id,
        extrusionWallAngle: calculateWallAngle(wall).toFixed(1) + '°'
      });
    }
  }
  
  // IMPROVED: Prioritize the provided front door wall, even if it's on a different floor
  let referenceFrontWall = frontDoorWall;
  
  if (!referenceFrontWall) {
    console.warn('🧭 No front door wall provided for orientation reference - this may cause incorrect orientations');
    // Only fall back to first wall if absolutely no front door wall is available
    // This should rarely happen in a properly configured building
    if (allWalls.length > 0) {
      referenceFrontWall = allWalls[0];
      console.warn('🧭 Using first available wall as reference - orientations may be incorrect');
    } else {
      console.warn('🧭 No walls available for orientation reference');
      return 'Unknown';
    }
  }
  
  // Calculate the angle of the front door wall
  const frontWallAngle = calculateWallAngle(referenceFrontWall);
  
  // Calculate the angle of this window's wall (using the corrected orientation wall)
  const windowWallAngle = calculateWallAngle(orientationWall);
  
  // Find the relative angle between this wall and the front door wall
  const relativeAngle = (windowWallAngle - frontWallAngle + 360) % 360;
  
  // The property orientation tells us which compass direction the front door faces
  // Apply this to get the absolute compass direction of this window
  const absoluteCompassDirection = (relativeAngle + propertyOrientation) % 360;
  
  const result = degreesToDirection(absoluteCompassDirection);
  
  console.log(`🧭 ${isExtrusionWall ? 'EXTRUSION-CORRECTED' : 'STANDARD'} Window orientation calculation:`, {
    windowId: window.id,
    windowFloor: (window as any).floor || 'unknown',
    isExtrusionWall: isExtrusionWall,
    originalWallUsed: orientationWall !== wall,
    frontDoorWallUsed: !!frontDoorWall,
    frontWallAngle: frontWallAngle.toFixed(1) + '° (front door wall)',
    windowWallAngle: windowWallAngle.toFixed(1) + '° (corrected window wall)',
    ...(isExtrusionWall && orientationWall !== wall && {
      extrusionWallAngle: calculateWallAngle(wall).toFixed(1) + '° (original extrusion wall - not used)'
    }),
    relativeAngle: relativeAngle.toFixed(1) + '° (relative to front door)',
    propertyOrientation: propertyOrientation.toFixed(1) + '° (compass: front door faces this direction)',
    absoluteCompassDirection: absoluteCompassDirection.toFixed(1) + '° (final compass direction)',
    finalOrientation: result,
    calculation: `(${windowWallAngle.toFixed(1)}° - ${frontWallAngle.toFixed(1)}°) + ${propertyOrientation.toFixed(1)}° = ${absoluteCompassDirection.toFixed(1)}° → ${result}`
  });
  
  return result;
};

// Calculate front door direction based on wall direction and door properties
export const calculateFrontDoorDirection = (door: DoorElement, wall: Wall): number => {
  const wallAngle = calculateWallAngle(wall);
  
  // Determine which direction the door opens based on opensIn and swingRight
  let doorDirection = wallAngle;
  
  if (door.opensIn) {
    // Door opens inward - faces the opposite direction of the wall's outward direction
    doorDirection = (wallAngle + 90) % 360;
  } else {
    // Door opens outward - faces the same direction as the wall's outward direction
    doorDirection = (wallAngle - 90 + 360) % 360;
  }
  
  return doorDirection;
};

// Auto-calculate orientations for all windows based on property orientation
export const autoCalculateWindowOrientations = (
  windows: WindowElement[],
  walls: Wall[],
  propertyOrientation: number,
  hasFrontDoor: boolean,
  orientationIsManuallySet: boolean,
  frontDoorElement?: DoorElement
): WindowElement[] => {
  // Only calculate orientations if we have BOTH essential pieces of information:
  // 1. A front door is set (establishes building layout)
  // 2. Property orientation has been properly set (compass reading)
  if (!hasFrontDoor || !orientationIsManuallySet) {
    console.log(`🧭 Skipping window orientation calculation - missing required data:`, {
      hasFrontDoor,
      orientationIsManuallySet,
      reason: !hasFrontDoor ? 'No front door set' : 'Property orientation not manually set'
    });
    
    // IMPORTANT: Return windows unchanged to preserve existing orientations
    // Don't clear orientations when data isn't ready - this could be during file loading
    return windows;
  }

  // Find the front door wall to use as reference
  let frontDoorWall: Wall | undefined;
  if (frontDoorElement && frontDoorElement.wallId) {
    frontDoorWall = walls.find(w => (w as any).stableId === frontDoorElement.wallId);
  }

  console.log(`🧭 Auto-calculating orientations for ${windows.length} windows with front door reference:`, {
    propertyOrientation: propertyOrientation.toFixed(1) + '°',
    hasFrontDoor,
    orientationIsManuallySet,
    frontDoorWallFound: !!frontDoorWall
  });
  
  return windows.map(window => {
    const wall = walls.find(w => (w as any).stableId === window.wallId);
    if (!wall) {
      console.warn(`🧭 Warning: Could not find wall for window ${window.id}`);
      return window;
    }
    
    const orientation = calculateWindowOrientation(window, wall, propertyOrientation, walls, frontDoorWall);
    
    return {
      ...window,
      orientation
    };
  });
};

// Test function to validate orientation calculations (for debugging)
export const validateWindowOrientations = (
  windows: WindowElement[],
  walls: Wall[],
  propertyOrientation: number
): {
  consistent: boolean;
  issues: string[];
  summary: { [orientation: string]: number };
} => {
  const issues: string[] = [];
  const orientationCounts: { [orientation: string]: number } = {};
  
  windows.forEach(window => {
    const wall = walls.find(w => (w as any).stableId === window.wallId);
    if (!wall) {
      issues.push(`Window ${window.id} has no associated wall`);
      return;
    }
    
    const orientation = calculateWindowOrientation(window, wall, propertyOrientation, walls);
    orientationCounts[orientation] = (orientationCounts[orientation] || 0) + 1;
    
    // Check for potential issues
    if (!orientation || orientation === 'Unknown') {
      issues.push(`Window ${window.id} has invalid orientation calculation`);
    }
  });
  
  return {
    consistent: issues.length === 0,
    issues,
    summary: orientationCounts
  };
};

// Find front door in elements array
export const findFrontDoor = (elements: any[]): DoorElement | null => {
  const doors = elements.filter(el => el.type === 'door') as DoorElement[];
  return doors.find(door => door.isFrontDoor) || null;
};

// Check if property has a front door
export const hasFrontDoor = (elements: any[]): boolean => {
  return findFrontDoor(elements) !== null;
};

// Calculate property orientation based on front door
export const calculatePropertyOrientationFromFrontDoor = (
  frontDoor: DoorElement,
  walls: Wall[]
): number => {
  const wall = walls.find(w => (w as any).stableId === frontDoor.wallId);
  if (!wall) return 0;
  
  return calculateFrontDoorDirection(frontDoor, wall);
};

// Auto-update property orientation when front door is set
export const autoUpdatePropertyOrientation = (
  frontDoor: DoorElement,
  walls: Wall[],
  currentOrientation: number,
  orientationHasBeenSet: boolean
): number => {
  // Only auto-update if orientation hasn't been manually set
  if (orientationHasBeenSet) return currentOrientation;
  
  return calculatePropertyOrientationFromFrontDoor(frontDoor, walls);
};

// Auto-calculate orientations for all windows across all floors based on property orientation
export const autoCalculateWindowOrientationsAllFloors = (
  allFloorPlans: Record<number, Wall[]>,
  allElements: FloorPlanElement[],
  propertyOrientation: number,
  hasFrontDoor: boolean,
  orientationIsManuallySet: boolean
): FloorPlanElement[] => {
  // Only calculate orientations if we have BOTH essential pieces of information:
  // 1. A front door is set (establishes building layout)
  // 2. Property orientation has been properly set (compass reading)
  if (!hasFrontDoor || !orientationIsManuallySet) {
    console.log(`🧭 Skipping building-wide window orientation calculation - missing required data:`, {
      hasFrontDoor,
      orientationIsManuallySet,
      reason: !hasFrontDoor ? 'No front door set' : 'Property orientation not manually set'
    });
    
    // IMPORTANT: Return elements unchanged to preserve existing orientations
    return allElements;
  }

  // Find the front door element to use as reference
  const frontDoorElement = findFrontDoor(allElements);
  let frontDoorWall: Wall | undefined;
  let frontDoorFloor: number | undefined;
  
  if (frontDoorElement && frontDoorElement.wallId) {
    // Find the wall across all floors
    for (const [floorNum, floorWalls] of Object.entries(allFloorPlans)) {
      const wall = floorWalls.find(w => (w as any).stableId === frontDoorElement.wallId);
      if (wall) {
        frontDoorWall = wall;
        frontDoorFloor = parseInt(floorNum);
        break;
      }
    }
  }

  console.log(`🧭 Auto-calculating orientations for all floors with front door reference:`, {
    propertyOrientation: propertyOrientation.toFixed(1) + '°',
    totalElements: allElements.length,
    frontDoorFound: !!frontDoorElement,
    frontDoorWallFound: !!frontDoorWall,
    frontDoorFloor: frontDoorElement?.floor
  });

  return allElements.map(element => {
    // Only process windows
    if (element.type !== 'window') {
      return element;
    }

    const window = element as WindowElement;
    const floorWalls = allFloorPlans[window.floor] || [];
    const wall = floorWalls.find(w => (w as any).stableId === window.wallId);
    
    if (!wall) {
      console.warn(`🧭 Warning: Could not find wall for window ${window.id} on floor ${window.floor}`);
      return window;
    }

    // CRITICAL FIX: For multi-floor buildings, we need to ensure all floors use the same front door reference
    // Pass ALL walls from ALL floors so the orientation calculation can find equivalent walls on different floors
    const allBuildingWalls = Object.values(allFloorPlans).flat();
    
    const orientation = calculateWindowOrientation(window, wall, propertyOrientation, allBuildingWalls, frontDoorWall);
    
    return {
      ...window,
      orientation
    };
  });
}; 